import type { ReactNode } from "react";
import { getEventNavigationData } from "~/lib/gtm/getGtmEventData";
import { AuthNavigationItem, BaseNavigationItem } from "~/types/navigation";

/**
 * Creates a standard navigation item with automatic GTM tracking
 * @param text - Display text for the navigation item
 * @param href - URL to navigate to
 * @param target - Optional link target (_blank for new tab)
 * @returns BaseNavigationItem with GTM data automatically configured
 */
export const createNavigationItem = (
    text: string,
    href: string,
    target?: "_blank" | "_top"
): BaseNavigationItem => ({
    text,
    href,
    gtmEventData: getEventNavigationData(text),
    target,
});

/**
 * Creates an authenticated navigation item with optional click handler
 * Used for items like "Chat Now" that need custom behavior
 * @param text - Display text for the navigation item
 * @param href - URL to navigate to
 * @param handleClick - Optional click handler function
 * @param target - Optional link target (_blank for new tab)
 * @returns Enhanced navigation item with click handler support
 */
export const createAuthNavigationItem = (
    text: string,
    href: string,
    handleClick?: () => void,
    target?: "_blank" | "_top"
): AuthNavigationItem => ({
    text,
    href,
    gtmEventData: getEventNavigationData(text),
    handleClick,
    target,
});

/**
 * Converts navigation item text to string for compatibility
 * @param text - Navigation item text (string or ReactNode)
 * @returns String representation of the text
 */
export const getNavigationTextAsString = (text: string | ReactNode): string => {
    return typeof text === "string" ? text : String(text);
};
